/**
 * @fileoverview "Add prep notes" — pick which upcoming session you are
 * planning for.
 *
 * The button on the class Overview cannot know which meeting an instructor has
 * in mind, and a class that meets twice a week has two plausible answers by
 * Wednesday. So the button opens the term's next meetings and the choice is one
 * tap, rather than sending everyone to a calendar to find the row they would
 * have picked here.
 *
 * Only sessions still in their prep window are offered. One that has already
 * started is not prep any more — it is a lesson waiting to be written — and
 * offering it here would promise a plan for a class that is under way.
 * A session that already carries a plan says so, because the commonest reason
 * to open this is to add to one.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Icon, ListRow, Tag } from "@/components/studio/primitives";
import { isPrepWindow, sessionStart } from "@/lib/prepNotes";

/** "Tue, Sep 24" — the weekday matters more than the year on a term list. */
function dayLabel(session) {
  const start = sessionStart(session);
  if (!start) return session?.date || "";
  return start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function clockLabel(session) {
  const start = sessionStart(session);
  if (!start || !session?.time_local) return null;
  return start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export default function PrepSessionPicker({ open, onOpenChange, sessions = [], onPick, now = new Date() }) {
  const choices = sessions
    .filter((s) => s.status !== "cancelled" && isPrepWindow(s, now))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="studio mx-4 max-w-[min(28rem,calc(100%-2rem))] border-[var(--studio-divider)] bg-[var(--studio-overlay)] p-0 text-[var(--text-primary)]"
        data-testid="prep-session-picker"
      >
        <DialogHeader className="px-[18px] pb-1 pt-5">
          <DialogTitle className="font-serif text-[19px] font-semibold text-[var(--text-primary)]">
            Which session?
          </DialogTitle>
        </DialogHeader>
        {choices.length === 0 ? (
          <p className="px-[18px] pb-6 pt-2 font-sans text-[14px] text-[var(--text-tertiary)]">
            Nothing left to plan for — every scheduled session has either started or been written up.
          </p>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto pb-3">
            {choices.map((session, i) => (
              <ListRow
                key={session.occurrence_id}
                borderBottom={i < choices.length - 1}
                title={dayLabel(session)}
                subtitle={[clockLabel(session), `${session.attending} expected`].filter(Boolean).join(" · ")}
                trailing={
                  session.my_prep?.body ? (
                    <Tag>Started</Tag>
                  ) : session.prep_note ? (
                    <span className="flex items-center gap-1.5 font-sans text-[13px] text-[var(--accent-yellow-text)]">
                      <Icon name="info" size={14} /> Note from the org
                    </span>
                  ) : null
                }
                onPress={() => onPick(session)}
                data-testid={`prep-pick-${session.occurrence_id}`}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
